// Slug → glyph mapping for expat-guide topic cards and headers.
// Mirrors mobile/src/components/expat-guide-topic-card SLUG_TO_ICON +
// expat-guide-slide-carousel EMOJI_TO_ICON. Icons drawn inline so we
// don't need to ship a new icon font on web.

import type { CSSProperties } from 'react';

interface IconProps {
  readonly size?: number;
  readonly color?: string;
  readonly style?: CSSProperties;
}

const svg = (size: number, color: string, style?: CSSProperties) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: color,
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
  style,
});

const Document = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h8" />
    <path d="M8 17h6" />
  </svg>
);
const IdCard = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2.2" />
    <path d="M14 10h5" />
    <path d="M14 14h5" />
    <path d="M5 17c0-1.5 2-3 4-3s4 1.5 4 3" />
  </svg>
);
const Card = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 10h18" />
    <path d="M7 15h4" />
  </svg>
);
const Medkit = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
    <path d="M12 11v6" />
    <path d="M9 14h6" />
  </svg>
);
const Shield = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const Home = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10h14V10" />
    <path d="M9 21v-6h6v6" />
  </svg>
);
const Briefcase = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
    <path d="M3 13h18" />
  </svg>
);
const Cash = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6 9h.01" />
    <path d="M18 15h.01" />
  </svg>
);
const School = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M3 9l9-5 9 5-9 5-9-5z" />
    <path d="M7 11v5c2 2 8 2 10 0v-5" />
    <path d="M21 9v6" />
  </svg>
);
const Chat = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M4 5h16v10H8l-4 4V5z" />
  </svg>
);
const Car = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M5 13l1.6-4.5A2 2 0 018.5 7h7a2 2 0 011.9 1.5L19 13" />
    <path d="M3 13h18v5h-3v2h-2v-2H8v2H6v-2H3v-5z" />
    <circle cx="7.5" cy="16" r="1" fill="currentColor" />
    <circle cx="16.5" cy="16" r="1" fill="currentColor" />
  </svg>
);
const People = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <circle cx="9" cy="8" r="3" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <path d="M14 20c0-2 1.5-4 4-4s3 1.5 3 3" />
  </svg>
);
const Wallet = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M16 13a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" fill="currentColor" />
    <path d="M3 9V7a2 2 0 012-2h12" />
  </svg>
);
const Scale = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M12 4v16" />
    <path d="M5 8h14" />
    <path d="M8 8l-3 6h6z" />
    <path d="M16 8l-3 6h6z" />
  </svg>
);
const Cart = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M3 4h2l2 12h11l2-8H6" />
    <circle cx="9" cy="20" r="1.5" />
    <circle cx="17" cy="20" r="1.5" />
  </svg>
);
const Leaf = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M5 19c0-9 5-14 14-14 0 9-5 14-14 14z" />
    <path d="M5 19l9-9" />
  </svg>
);
const Palette = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M12 3a9 9 0 100 18c1.5 0 2-1.5 1-2.5-1-1 0-2.5 1.5-2.5h1c2.5 0 4.5-2 4.5-4.5C20 7.5 16.5 3 12 3z" />
    <circle cx="7.5" cy="11" r="1" fill="currentColor" />
    <circle cx="11" cy="7" r="1" fill="currentColor" />
    <circle cx="15" cy="7.5" r="1" fill="currentColor" />
  </svg>
);
const Globe = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a13 13 0 010 18" />
    <path d="M12 3a13 13 0 000 18" />
  </svg>
);
const Phone = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <path d="M11 18h2" />
  </svg>
);
const Book = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M4 4h12a3 3 0 013 3v13H7a3 3 0 01-3-3V4z" />
    <path d="M4 4v13a3 3 0 003 3" />
    <path d="M8 8h7" />
    <path d="M8 12h7" />
  </svg>
);
const Time = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const Calendar = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
    <path d="M4 10h16" />
  </svg>
);
const Mail = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 7l9 7 9-7" />
  </svg>
);
const Bulb = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M9 18h6" />
    <path d="M10 21h4" />
    <path d="M12 3a6 6 0 014 10.5c-1 .8-1.5 2-1.5 3.5h-5c0-1.5-.5-2.7-1.5-3.5A6 6 0 0112 3z" />
  </svg>
);
const Pin = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M12 22s7-7 7-12a7 7 0 10-14 0c0 5 7 12 7 12z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);
const Check = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12l3 3 5-6" />
  </svg>
);
const Warning = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M12 3l10 18H2L12 3z" />
    <path d="M12 10v5" />
    <circle cx="12" cy="18" r="0.5" fill="currentColor" />
  </svg>
);
const Info = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <circle cx="12" cy="8" r="0.5" fill="currentColor" />
  </svg>
);
const Lock = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
);
const Heart = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <path d="M12 21s-7-5-7-11a4 4 0 017-2 4 4 0 017 2c0 6-7 11-7 11z" />
  </svg>
);
const Settings = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
  </svg>
);
const Bicycle = (p: IconProps) => (
  <svg {...svg(p.size ?? 22, p.color ?? 'currentColor', p.style)}>
    <circle cx="6" cy="17" r="3" />
    <circle cx="18" cy="17" r="3" />
    <path d="M6 17l4-7h5l3 7" />
    <path d="M10 10l3-3h2" />
  </svg>
);

// --- Slug → component ------------------------------------------------------

const SLUG_TO_ICON: Record<string, (p: IconProps) => JSX.Element> = {
  'residence-permits': Document,
  'visas-permits': Document,
  'personnummer': IdCard,
  'cpr-number': IdCard,
  'banking-finance': Card,
  'banking': Card,
  'healthcare': Medkit,
  'social-security': Shield,
  'benefits': Shield,
  'housing': Home,
  'employment': Briefcase,
  'taxes': Cash,
  'education': School,
  'swedish-sfi': Chat,
  'danish-language': Chat,
  'driving-transport': Car,
  'driving': Car,
  'integration': People,
  'family-children': People,
  'family-parenting': People,
  'pension': Wallet,
  'pensions': Wallet,
  'legal-admin': Scale,
  'consumer-rights': Cart,
  'environment': Leaf,
  'culture-society': Palette,
  'culture': Palette,
  'networking': Globe,
  'digital-sweden': Phone,
  'digital-setup': Phone,
};

export function GuideSlugIcon(props: IconProps & { readonly slug: string }) {
  const Comp = SLUG_TO_ICON[props.slug] ?? Book;
  return <Comp size={props.size} color={props.color} style={props.style} />;
}

// --- Slide visual emoji → component ----------------------------------------

const EMOJI_TO_ICON: Record<string, (p: IconProps) => JSX.Element> = {
  '📝': Document,
  '📋': Document,
  '🏥': Briefcase,
  '🏛️': Briefcase,
  '🏛': Briefcase,
  '💳': Card,
  '💰': Cash,
  '🏠': Home,
  '💼': Briefcase,
  '🎓': School,
  '🔒': Lock,
  '📱': Phone,
  '🌍': Globe,
  '🌎': Globe,
  '🌏': Globe,
  '❤️': Heart,
  '🚗': Car,
  '🚴': Bicycle,
  '🏫': School,
  '🔑': Lock,
  '⚙️': Settings,
  '📞': Phone,
  '📧': Mail,
  '🛒': Cart,
  '🏃': People,
  '⚽': People,
  '🎭': Palette,
  '🎉': Bulb,
  '📚': Book,
  '📊': Info,
  '📈': Info,
  '✅': Check,
  '✔️': Check,
  '⚠️': Warning,
  'ℹ️': Info,
  '💡': Bulb,
  '🕒': Time,
  '📅': Calendar,
  '📍': Pin,
};

export function GuideEmojiIcon(props: IconProps & { readonly emoji: string }) {
  const Comp = EMOJI_TO_ICON[props.emoji] ?? Info;
  return <Comp size={props.size} color={props.color} style={props.style} />;
}
